import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Stack,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { MdEmail, MdPhone, MdBadge, MdBusiness } from "react-icons/md";

const InfoRow = ({ icon, label, value, isDark }) => (
  <Stack direction="row" spacing={1.25} alignItems="center">
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          fontWeight: 600,
          fontSize: "0.65rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.3 }}
        noWrap
        title={value}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Stack>
);

const EmployeeInfoCard = ({ company }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  if (!company) return null;

  const initials =
    `${company.first_name?.[0] || ""}${
      company.last_name?.[0] || ""
    }`.toUpperCase() || "E";
  const fullName =
    `${company.first_name || ""} ${company.last_name || ""}`.trim() ||
    "Employee";

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: -15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        justifyContent: "center",
        background: isDark
          ? "linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
          : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        border: `1px solid ${
          isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"
        }`,
        boxShadow: isDark
          ? "0 10px 30px rgba(0, 0, 0, 0.35)"
          : "0 10px 30px rgba(15, 23, 42, 0.05)",
      }}
    >
      {/* Header row: avatar + name + employee code */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 44,
            height: 44,
            fontWeight: 800,
            fontSize: "1rem",
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            color: "#fff",
          }}
        >
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, lineHeight: 1.2 }}
            noWrap
          >
            {fullName}
          </Typography>
          <Chip
            icon={<MdBadge style={{ color: "#10b981" }} />}
            label={company.employee_code || "—"}
            size="small"
            sx={{
              mt: 0.5,
              fontWeight: 700,
              fontSize: "0.7rem",
              backgroundColor: isDark ? alpha("#10b981", 0.15) : "#d1fae5",
              color: "#059669",
              border: `1px solid ${isDark ? alpha("#10b981", 0.3) : "#a7f3d0"}`,
              borderRadius: "6px",
            }}
          />
        </Box>
      </Stack>

      {/* Divider */}
      <Box
        sx={{
          height: "1px",
          background: isDark
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.06)",
        }}
      />

      {/* Info rows */}
      <Stack spacing={1}>
        <InfoRow
          icon={<MdBusiness size={16} color="#6366f1" />}
          label="Company"
          value={company.company_name}
          isDark={isDark}
        />
        <InfoRow
          icon={<MdEmail size={16} color="#3b82f6" />}
          label="Email"
          value={company.email}
          isDark={isDark}
        />
        <InfoRow
          icon={<MdPhone size={16} color="#10b981" />}
          label="Phone"
          value={company.phone}
          isDark={isDark}
        />
      </Stack>
    </Box>
  );
};

export default EmployeeInfoCard;
